import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'common/hooks/state';
import { RootState } from 'common/store/store';
import { linkPath, isBypassEmail } from 'common/constants';
import { Purchases as PurchasesWeb } from '@revenuecat/purchases-js';
import { Capacitor } from '@capacitor/core';
import {
  UserIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  UserGroupIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { lockSession, selectLockEnabled, setWebAuthSetUp } from 'common/store/lockSlice';
import { removeStoredCredential } from 'common/services/webAuthnService';
import { useLogoutActionMutation } from 'features/MenuBar/MenuBarService';
import { useGetProfilePictureQuery } from 'features/Sharing/SharingService';
import { usePaywall } from 'features/pricing/showPaywall';
import DialogBox from 'common/designSystem/Modal/DialogBox';
import { Logout } from 'assets/images';
import lang from 'common/lang';
import { clearCredentials } from 'features/login/authSlice';
import { baseApi } from 'common/store/baseApi';
import { resetShepherd } from 'features/Shepherd/shepherdSlice';
import { clearSpotlight } from 'common/utils/spotlight';
import { clearWidgetSnapshot } from 'common/utils/widgetSnapshot';
import { trackEvent, MixpanelEventName } from 'common/utils/mixpanel/eventTriggers';
import { safeStorage } from 'common/utils/safeStorage';

interface UserDropdownMenuProps {
  className?: string;
}

export const UserDropdownMenu: React.FC<UserDropdownMenuProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const { showPaywall } = usePaywall();
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [openLogoutModal, setOpenLogoutModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { menuBarPageText } = lang;

  const user = useAppSelector((state: RootState) => state.auth.user);
  const lockEnabled = useAppSelector(selectLockEnabled);
  const isNativeApp = Capacitor.isNativePlatform();
  const firstName = user?.user?.first_name || user?.first_name || '';
  const email = user?.user?.email || user?.email || '';

  // Fetch profile picture from dedicated API endpoint
  const { data: profilePictureData } = useGetProfilePictureQuery();
  const profilePicture = profilePictureData?.data?.profile_picture || null;

  const [hasSubscribed, setHasSubscribed] = useState(false);

  // Check subscription status from RevenueCat and backend
  useEffect(() => {
    const checkSubscription = async () => {
      const backendSubscribed = user?.user?.has_subscribed || user?.has_subscribed;
      if (isBypassEmail(email)) {
        setHasSubscribed(true);
        return;
      }
      if (Capacitor.isNativePlatform()) {
        try {
          const customerInfo = await Purchases.getCustomerInfo();
          const premiumEntitlement = customerInfo?.customerInfo?.entitlements?.active['Doqit Pro'];
          setHasSubscribed(!!premiumEntitlement || !!backendSubscribed);
        } catch (error) {
          setHasSubscribed(!!backendSubscribed);
        }
      } else {
        try {
          if (!PurchasesWeb.isConfigured()) {
            setHasSubscribed(!!backendSubscribed);
            return;
          }
          const purchases = PurchasesWeb.getSharedInstance();
          const userId = user?.user?.id || user?.id;
          if (userId) {
            await purchases.identifyUser(userId.toString());
            safeStorage.setItem('rc_user_id', userId.toString());
          }
          const customerInfo = await purchases.getCustomerInfo();
          const rcSubscribed = !!customerInfo?.entitlements?.active?.['Doqit Pro'];
          setHasSubscribed(rcSubscribed || !!backendSubscribed);
        } catch (error) {
          setHasSubscribed(!!backendSubscribed);
        }
      }
    };
    checkSubscription();
  }, [user]);

  const [logoutAction] = useLogoutActionMutation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    // Shared-device hygiene: a WebAuthn credential registered by this
    // browser/OS profile must not silently carry over to whoever logs in
    // next on the same device — clear both the localStorage credential and
    // the persisted lock-slice flags that mark it "set up".
    removeStoredCredential();
    dispatch(setWebAuthSetUp({ isSetUp: false }));
    // Explicit sign-out: a second account logging in on this browser must
    // not inherit this account's completed-tour flag. Dispatched
    // unconditionally, not gated on the logout API call succeeding.
    dispatch(resetShepherd());
    // Spotlight and the widget snapshot are also purged unconditionally,
    // right here, rather than left to clearCredentials below: that dispatch
    // only fires once the logout API call resolves successfully, whereas a
    // shared-device sign-out must not leave document names searchable or on
    // the home screen just because the network request failed or is slow.
    // clearCredentials repeats both purges on success - harmless, since both
    // are idempotent no-ops off iOS and safe to call twice. No-op off iOS;
    // never throws.
    clearSpotlight();
    clearWidgetSnapshot();

    // Reset RevenueCat user on logout
    if (!Capacitor.isNativePlatform()) {
      try {
        if (PurchasesWeb.isConfigured()) {
          const purchases = PurchasesWeb.getSharedInstance();
          const newAnonId = PurchasesWeb.generateRevenueCatAnonymousAppUserId();
          safeStorage.setItem('rc_anonymous_id', newAnonId);
          safeStorage.removeItem('rc_user_id');
          await purchases.changeUser(newAnonId);
        }
      } catch (err) {
        // Ignore error on web logout
      }
    }

    try {
      // Any resolved response is treated as a successful sign-out here, not
      // gated on the response body's own `status` field - a user who
      // pressed "Log out" must never stay signed in locally just because a
      // typed boolean on an HTTP 200 said so. Consistent across all three
      // sign-out paths (this handler, MenuBar's onLogoutHandler,
      // LockScreen's handleSignOut) - see each site's own comment; none
      // should re-diverge from this.
      await logoutAction().unwrap();
      // Only after the API call resolves: logoutAction's queryFn deletes
      // the device push token and POSTs /logout before this point, and
      // both need a still-valid session token, so clearing credentials
      // any earlier would revoke the client's own auth before it could
      // ask the server to revoke it. Mirrors the genuine-401 path in
      // rtkErrorHandler.ts - clearCredentials wipes the auth slice (and,
      // via its reducer, Sentry, pending-path, Spotlight, the widget
      // snapshot and the native shared preferences); resetApiState drops
      // every RTK Query cache entry so no other account's document data
      // lingers in memory for whoever signs in next on this device.
      trackEvent(MixpanelEventName.loggedOut, { source: 'dropdown' });
      dispatch(clearCredentials());
      dispatch(baseApi.util.resetApiState());
      setOpenLogoutModal(false);
      // replace, not push: a plain push leaves the current page in
      // history, so Android back from /login would remount PersistLogin.
      // It no longer has a token to find (clearCredentials just wiped
      // it), but there is no reason to rely on that alone - replace also
      // removes the authenticated shell from history entirely.
      navigate(linkPath.login, { replace: true });
    } catch (error) {
      // Same rule as MenuBar's onLogoutHandler: pressing "Log out" signs you
      // out even if the server call fails. Swallowing the error and doing
      // nothing left the user signed in with their vault, Spotlight index
      // and tour flag already wiped (869eewg6e).
      console.error('Logout request failed, signing out locally anyway:', error);
      trackEvent(MixpanelEventName.loggedOut, { source: 'dropdown' });
      dispatch(clearCredentials());
      dispatch(baseApi.util.resetApiState());
      setOpenLogoutModal(false);
      navigate(linkPath.login, { replace: true });
    }
  };

  const menuItems = [
    {
      label: 'My profile',
      icon: <UserIcon className="w-5 h-5" />,
      onClick: () => {
        setIsOpen(false);
        if (!hasSubscribed) {
          showPaywall({ source: 'menu_profile_gate' });
          return;
        }
        navigate(linkPath.userProfile);
      },
    },
    {
      label: 'My settings',
      icon: <Cog6ToothIcon className="w-5 h-5" />,
      onClick: () => {
        setIsOpen(false);
        if (!hasSubscribed) {
          showPaywall({ source: 'menu_settings_gate' });
          return;
        }
        navigate(linkPath.menuBar);
      },
    },
    {
      label: 'My sharing',
      icon: <UserGroupIcon className="w-5 h-5" />,
      onClick: () => {
        setIsOpen(false);
        if (!hasSubscribed) {
          showPaywall({ source: 'menu_sharing_gate' });
          return;
        }
        navigate(linkPath.mySharing);
      },
    },
    {
      label: 'Switch account',
      icon: <UserIcon className="w-5 h-5" />,
      onClick: () => {
        // For now, just close - can implement later
        setIsOpen(false);
      },
      disabled: true,
    },
    // Lock session option - web only, when lock is enabled
    ...(!isNativeApp && lockEnabled ? [{
      label: 'Lock session',
      icon: <LockClosedIcon className="w-5 h-5" />,
      onClick: () => {
        setIsOpen(false);
        dispatch(lockSession());
      },
    }] : []),
    {
      label: 'Log out',
      icon: <ArrowRightOnRectangleIcon className="w-5 h-5" />,
      onClick: () => {
        setIsOpen(false);
        setOpenLogoutModal(true);
      },
      danger: true,
    },
  ];

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-gray-100 transition-colors"
        data-tour="avatar"
      >
        {profilePicture ? (
          <img
            src={profilePicture}
            alt={firstName}
            className="w-9 h-9 rounded-full object-cover border-2 border-doqit-button-primary"
          />
        ) : (
          <div className="w-9 h-9 rounded-full bg-doqit-button-primary flex items-center justify-center text-white font-medium">
            {firstName.charAt(0).toUpperCase()}
          </div>
        )}
        <span className="text-doqit-purple font-medium hidden web-large:inline">
          {firstName}
        </span>
        <svg
          className={`w-4 h-4 text-doqit-purple transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
          {menuItems.map((item, index) => (
            <button
              key={item.label}
              type="button"
              onClick={item.onClick}
              disabled={item.disabled}
              className={`
                w-full flex items-center gap-3 px-4 py-3 text-left transition-colors
                ${item.danger ? 'text-red-500 hover:bg-red-50' : 'text-doqit-purple hover:bg-gray-50'}
                ${item.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                ${index === menuItems.length - 1 ? 'border-t border-gray-100 mt-1 pt-3' : ''}
              `}
            >
              {item.icon}
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Logout Modal */}
      {openLogoutModal && (
        <DialogBox
          heading={menuBarPageText.logout}
          subHeading={menuBarPageText.logoutPopupSubHeading}
          isOpen={true}
          primaryButtonText={menuBarPageText.deletePrimaryButtonText}
          secondaryButtonText={menuBarPageText.logoutSecondaryButtonText}
          onCloseHandler={() => setOpenLogoutModal(false)}
          onHandlePrimaryClick={() => setOpenLogoutModal(false)}
          onHandleSecondaryClick={handleLogout}
          headingIcon={<img src={Logout} className="w-200 h-120" alt="logout" />}
          isMyProfileModal={true}
        />
      )}
    </div>
  );
};

export default UserDropdownMenu;
